import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import PermanentSidebar from './components/PermanentSidebar';
import ExpandableSidebar from './components/ExpandableSidebar';

import findDeep from '../../design-system/utils/findDeep';

import { useB2BPermissions } from '../../context/B2BPermissionsContext';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
}));

function findNodeById(data, targetId) {
    for (const item of data) {
      if (item.id === targetId) {
        return item;
      } else if (item.children && Array.isArray(item.children)) {
        const foundInChildren = findNodeById(item.children, targetId);
        if (foundInChildren) {
          return item; // Devuelve el nodo padre que envuelve al nodo encontrado en children
        }
      }
    }
    return null; // Devuelve null si no se encuentra el ID
  }

function Sidebar() {
    const location = useLocation();
    const history = useHistory();
    const classes = useStyles();

    // Páginas dinámicas desde el endpoint de permisos (ya filtradas por el backend).
    // Fail-closed: si la carga está en curso o la Lambda de permisos falla, el
    // context devuelve [] y aquí no se renderiza ningún módulo (ver abajo el
    // early-return cuando filteredPages está vacío).
    const { dynamicPages } = useB2BPermissions();
    const filteredPages = dynamicPages;

    // Determinar ruta inicial basada en módulos permitidos
    const getDefaultPath = () => {
        const source = filteredPages;
        const findFirstPath = (items) => {
            for (const item of items) {
                if (item.path && item.component) return item.path;
                if (item.children) {
                    const found = findFirstPath(item.children);
                    if (found) return found;
                }
            }
            return null;
        };
        return findFirstPath(source) || location.pathname;
    };

    const currentPath = location.pathname === '/' ? getDefaultPath() : location.pathname;

    const [subMenu, setSubMenu] = React.useState([]);
    const [menu, setMenu] = React.useState([]);

    React.useEffect(() => {
        // Si no hay módulos, no hacer nada
        if (filteredPages.length === 0) return;

        // Regla anti-bucle: NUNCA hacer history.replace hacia la URL real actual.
        // Las comparaciones deben ser contra location.pathname (la URL del
        // navegador), NO contra currentPath (un valor "resuelto" que en '/' ya
        // apunta al primer módulo). Comparar contra currentPath permitía replaces
        // redundantes que, con re-renders, se volvían un bucle infinito.
        const realPath = location.pathname;

        // Si la ruta actual no está en los módulos permitidos, redirigir
        const activeNode = findDeep(filteredPages,'path', currentPath, 'children');
        if (!activeNode) {
            const defaultPath = getDefaultPath();
            if (defaultPath && defaultPath !== realPath) {
                history.replace(defaultPath);
                return;
            }
        }

        // Si estamos en una ruta raíz sin componente, redirigir al primer hijo navegable
        if (realPath === '/reportes' || realPath === '/b2b' || realPath === '/admin') {
          const rootItem = filteredPages.find(p => p.path === realPath);
          if (rootItem && rootItem.children) {
            const findFirstPath = (items) => {
              for (const item of items) {
                if (item.path && item.component) return item.path;
                if (item.children) {
                  const found = findFirstPath(item.children);
                  if (found) return found;
                }
              }
              return null;
            };
            const firstPath = findFirstPath(rootItem.children);
            if (firstPath && firstPath !== realPath) {
              history.replace(firstPath);
              return;
            }
          }
        }

        // obtengo los items activos
        if (activeNode) {
          const parentNode = findNodeById(filteredPages, activeNode.id);

          // Función para marcar active recursivamente
          const markActive = (items, activeId) => {
            return items.map(it => {
              const newItem = { ...it };
              if (newItem.children) {
                newItem.children = markActive(newItem.children, activeId);
                // Un padre está activo si alguno de sus hijos está activo
                newItem.active = newItem.id === activeId || newItem.children.some(child => child.active);
              } else {
                newItem.active = newItem.id === activeId;
              }
              return newItem;
            });
          };

          const _subMenu = !parentNode.children ? [] : markActive(parentNode.children, activeNode.id);

          const _menu = filteredPages.map(({ component, ...rest }) => {
              rest.active = parentNode.id === rest.id;
              return rest;
          });
          
          setSubMenu(_subMenu);
          setMenu(_menu);
        }
    }, [currentPath, filteredPages]);


    const hanldeClickMenuItem = (menuItem) => {
        const menuItemSelected = filteredPages.find((m) => m.id === menuItem.id);
        if (!menuItemSelected) return;
        if (!menuItemSelected) {
          return;
        }
        
        // Buscar el primer path disponible que tenga componente
        const findFirstPathWithComponent = (item) => {
          // Si tiene componente y path, usar este path
          if (item.path && item.component) {
            return item.path;
          }
          // Si tiene hijos, buscar recursivamente
          if (item.children && item.children.length > 0) {
            for (const child of item.children) {
              const found = findFirstPathWithComponent(child);
              if (found) return found;
            }
          }
          return null;
        };
        
        const goTo = findFirstPathWithComponent(menuItemSelected);
        if (goTo) {          history.push(goTo);
        } else if (menuItemSelected.path) {
          // Si no hay hijos con componente pero tiene path, navegar ahí
          history.push(menuItemSelected.path);
        }
    };

    const hanldeClickSubMenuItem = (menuItem) => {
        // Si tiene path, navegar directamente
        if (menuItem.path) {
          history.push(menuItem.path);
          return;
        }
        
        // Si no tiene path pero tiene hijos, navegar al primer hijo con path
        if (menuItem.children && menuItem.children.length > 0) {
          const findFirstPath = (items) => {
            for (const item of items) {
              if (item.path && item.component) return item.path;
              if (item.children) {
                const found = findFirstPath(item.children);
                if (found) return found;
              }
            }
            return null;
          };
          const firstPath = findFirstPath(menuItem.children);
          if (firstPath) {
            history.push(firstPath);
          }
        }
    };

    // Obtener el título del menú activo
    const activeMenuTitle = menu?.find(m => m.active)?.label || '';

    return (
        <div className={classes.root}>
        <PermanentSidebar menu={menu} onClickItem={hanldeClickMenuItem}/>
        {subMenu.length > 0 && (
          <ExpandableSidebar 
            menu={subMenu} 
            onClickItem={hanldeClickSubMenuItem}
            title={activeMenuTitle}
          />
        )}
        </div>
    );
}

export default Sidebar;
